<template>
  <div class="app-publishing-dashboard grid">
    <app-error class="is--inline col-12"
      v-if="errors['new-publication']" 
      v-bind="errors['new-publication']"
    />
    <slot></slot>
      <!-- publication builder component -->
    <publishing-edit-form v-if="isCreating" :project-id="project.id" :driver="project.driver" :source="publication" class="col-12 col-lg-8"
      :disabled="isSaving[publication.id]" :isClosed="false" @close="isCreating = false" @update="update"
      @save="save" @remove="remove">
    </publishing-edit-form>
    <div class="col-12 col-lg-4">

      <div class="app-publishing-dashboard__resources page-main__section">
        <div class="app-publishing-dashboard__resources-header page-main__section-header">
          <h3 class="page-main__section-title">Quick Resources</h3>
          <b-dropdown v-if="hasLocales" split @click="onPreviewPressed()" text="Preview Course" class="element-button--secondary-dropdown">
              <b-dropdown-item v-for="(locale, index) in locales"
                  :key="`locale-${index}`"
                  @click="onPreviewPressed(locale.locale)"
                  >Preview {{ locale.language_code }} translation
              </b-dropdown-item>
          </b-dropdown>
          <button v-else
              @click="onPreviewPressed()"
              class="element-button--secondary element-button">
              Preview Course
          </button>
        </div>
        <div v-if="!isClientRole" :class="['app-accordion', isConfiguring[publication.id] ? 'is--open' : '']">
          <app-button class="app-accordion__header" title="Custom Configuration" @click="setConfiguring(publication.id, !isConfiguring[publication.id])">
            <h4 class="app-accordion__title">Course Config Settings</h4>
            <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
          </app-button>
          <div v-show="isConfiguring[publication.id]" class="app-accordion__body">
            <p class="element-instructions">Define config settings, such as setting up Issue Tracker, that will be applied to this course.</p>
            <small>{{ isPublicationConfigSaving[this.publication.id] ? "Saving Configuration..." : "" }}</small>
            <code-editor name="configuration" :ref="`configuration-${publication.id}`"
              :value="getJSONString(publication.configuration)"
              :placeholder="'{\n\t&quot;key&quot;: &quot;value&quot;\n}'" language="json" rows="10"
              @change="updateConfiguration(publication.id, $event)" class="d-block w-100">
            </code-editor>
          </div>
        </div>
      </div>

      <div class="app-publishing-dashboard__publishing page-main__section">
        <div class="app-publishing-dashboard__publishing-header page-main__section-header">
          <h3 class="page-main__section-title">Publishing (English)
          <div v-if="!publication.published_at" class="element-badge">Draft</div>
          <div v-if="publication.published_at">
            <div v-if="
              publication.content_updated_at > publication.published_at
            " class="element-badge is--error"
              :title="`${publication.content_updated_count} objects updated since last publish`">
              Published: Outdated
            </div>
            <div v-else class="element-badge is--success"
              :title="`${publication.content_updated_count} objects updated since last publish`">
              Published: Up-to-Date
            </div>
          </div>
          </h3>
          <app-button theme="secondary" :disabled="isPublishing[publication.id]"
            @click="publish(publication)" title="Publish">
            {{ isPublishing[publication.id] ? "Publishing..." : "Publish Course" }}
          </app-button>
        </div>

        <p class="app-publishing-dashboard__last-published" v-if="publication.published_at">
          <strong>Last Published: </strong>{{formatTime(publication.published_at)}}
        </p>
        <p class="app-publishing-dashboard__content-edited" v-if="publication.content_updated_at">
          <strong>Content Modified: </strong>{{formatTime(publication.content_updated_at)}}
        </p>
        <div role="alert" v-if="publishingStatus[publication.id]">
          <div class="" v-if="publishingStatus[publication.id].progress === 1">
            Publishing Completed!
          </div>
          <div class="" v-else>
            {{ publishingStatus[publication.id].text }}
          </div>
        </div>
        <div class="app-publishing-dashboard__downloads">
          <h4 class="app-publishing-dashboard__downloads-title page-main__section-subtitle">Downloads</h4>
          <a v-if="publication.path" class="element-button--text element-button" title="Download"
            :href="`/publications/${publication.id}/download`">
            SCORM
          </a>
          <a class="element-button--text element-button" title="Script Export"
            :href="`/publications/${publication.id}/script`">
            Script (Old)
          </a>
          <a class="element-button--text element-button" title="Script Export"
            :href="`/publications/${publication.id}/script-v2`">
            Script (New)
          </a>
        </div>
      </div>

      <div v-if="hasLocales" class="app-publishing-dashboard__translations page-main__section">
        <div class="app-publishing-dashboard__translations-header page-main__section-header">
          <h3 class="page-main__section-title">Publishing (Translations)</h3>
          <b-dropdown split @click="publish(publication, 'ALL')" text="Publish All" class="element-button--secondary-dropdown">
              <b-dropdown-item v-for="(locale, index) in locales"
                  :key="`locale-${index}`"
                  @click="publish(publication, locale.locale)"
                  >Publish {{ locale.language_code }} only
              </b-dropdown-item>
          </b-dropdown>
          <p class="element-instructions">Use the dropdown arrow to generate a ZIP file that only includes assets relevant to one selected language, or select Generate All Packages to generate individual ZIP files for all available languages. Resulting language-specific ZIP file sizes will be significantly reduced in the case of multiple locales.</p>
        </div>
        <div class="app-publishing-dashboard__translations-list">
          <div class="app-publishing-dashboard__translation" v-for="(zip, index) in publication?.publication_zips">
            <h4 class="app-publishing-dashboard__translation-title">{{ getLanguageLabel(zip.locale) }}</h4>
            <span v-if="
              publication.content_updated_at > zip.updated_at
            " class="element-badge is--error">
              Published: Outdated
            </span>
            <span v-else class="element-badge is--success">
              Published: Up-to-Date
            </span>
            <p class="app-publishing-dashboard__translation-published"><strong>Last Published:</strong> {{ formatTime(zip.updated_at) }}</p>
            <a class="element-button--text element-button" title="Download"
            :href=zip.url>
              SCORM
            </a>
            <app-button class="app-publishing-dashboard__translation-delete element-button--icon" title="Delete Link"
              @click="deleteLocalizedZip(zip.id)"
              >
              <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
            </app-button>
          </div>
          <div v-if="!publication.publication_zips.length">No published zips</div>
        </div>
      </div>
      <div v-if="!hasBasicAccess">
        <public-review-generate ref="manager"
                    :publication="publication"
                    :project="project"
                    :enable-settings="true"
                    :enable-title="true"
                    class="page-main__section app-public-review-generate--full"
                    @setupEcho="setupEchoListener($event)">
                    >
        </public-review-generate>
      </div>
    </div>
    <div v-show="showProjectConfiguration" class="page-main__section">
      <!-- publication builder component -->
      <div class="page-main__section-header">
          <h3 class="page-main__section-title">Project Config Settings</h3>
          <p class="element-instructions">Define config settings, such as setting up Issue Tracker, that will be applied to all courses in this project.</p>
      </div>
      <code-editor ref="project-configuration" :value="getJSONString(projectConfiguration)"
        :placeholder="'{\n\t&quot;key&quot;: &quot;value&quot;\n}'" language="json" rows="10"
        @change="updateProjectConfiguration" class="d-block w-100">
      </code-editor>
    </div>
    <div v-show="showProjectConfiguration" class="page-main__section">
      <!-- publication builder component -->
      <div class="page-main__section-header">
          <h3 class="page-main__section-title">Project Config Settings</h3>
          <p class="element-instructions">Define config settings, such as setting up Issue Tracker, that will be applied to all courses in this project.</p>
      </div>
      <code-editor ref="project-configuration" :value="getJSONString(projectConfiguration)"
        :placeholder="'{\n\t&quot;key&quot;: &quot;value&quot;\n}'" language="json" rows="10"
        @change="updateProjectConfiguration" class="d-block w-100">
      </code-editor>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import Echo from '../helpers/echo'
import api from '../api/publisher'
import { mapGetters } from 'vuex'
import PublishingEditForm from './PublishingEditForm'

export default {
  components: { PublishingEditForm },
  props: {
    project: {
      type: Object,
    },
    item: {
      type: Object,
      default: () => Object({ data: [] }),
    },
  },
  data() {
    return {
      itemsPerPage: 20,
      isExporting: false,
      isCreating: true,
      isConfiguring: {},
      isPreviewConfiguring: {},
      isEditing: {},
      isSaving: {},
      isPublishing: {},
      isLinkCreating: false,
      isLinkCopying: {},
      isLinkReseting: {},
      isLinkRemoving: {},
      isLinkNameSaving: {},
      isLinkConfigSaving: {},
      isPublicationConfigSaving: {},
      isLinkLocked: {},
      isPublishAndDownload: false,
      showProjectConfiguration: false,
      projectConfiguration: this.project.configuration,
      errors: {},
      publishingStatus: {},
      publication: this.item
    }
  },
  computed: {
    ...mapGetters([
      'hasBasicAccess'
    ]),
    locales: state => state.project?.locales ?? [],
    hasLocales() {
      return this.locales.length > 0
    },
    isAdmin() {
      return this.$store.getters.isAdmin
    },
    isClientRole() {
      return this.$store.getters.isClientRole
    },
    sortedPublications() {
      // return _.reverse(_.sortBy(this.publications, 'created_at'))
      return _.sortBy(this.publication, 'name')
    }
  },
  methods: {
    setSaving(id, value = true) {
      if (value) {
        this.clearErrors(id)
      }
      this.isSaving[id] = value
    },
    setPublishing(id, value = true) {
      this.isPublishing[id] = value
    },
    setPublicationConfigSaving(id, value = true) {
      this.isPublicationConfigSaving[id] = value
    },
    setLinkCopying(id, value = true) {
      this.isLinkCopying[id] = value
    },
    setLinkResetting(id, value = true) {
      this.isLinkReseting[id] = value
    },
    setLinkRemoving(id, value = true) {
      this.isLinkRemoving[id] = value
    },
    setLinkNameSaving(id, value = true) {
      this.isLinkNameSaving[id] = value
    },
    setLinkConfigSaving(id, value = true) {
      this.isLinkConfigSaving[id] = value
    },
    setLinkLocking(id, value) {
      this.isLinkLocked[id] = value
    },
    setConfiguring(id, value = true) {
      this.isConfiguring[id] = value
    },
    // setPreviewConfiguring(id, value = true) {
    //   this.isPreviewConfiguring[id] = value
    // },
    setEditing(id, value = true) {
      this.isEditing[id] = value
    },
    setErrors(id, value) {
      this.errors[id] = value
    },
    toggleProjectConfiguration(value) {
      if (_.isBoolean(value)) {
        this.showProjectConfiguration = value
      } else {
        this.showProjectConfiguration = !this.showProjectConfiguration
      }
    },
    clearErrors(id) {
      this.setErrors(id, null)
    },
    formatTime(ts) {
      return moment(ts).format('LLL')
    },
    getJSONString(obj) {
      if (!obj) {
        return ''
      }
      return JSON.stringify(obj, null, 2)
    },
    getLanguageLabel(locale) {
      const item = this.locales.find(item => item.locale === locale);
      return item ? item.language_code : null;
    },
    setupEchoListener(publication) {
      // subscribe to channel for each course
      Echo.private(`App.Models.Publication.${publication.id}`).notification(
        async (notification) => {
          this.publishingStatus[publication.id] = notification
          const model = _.get(notification, 'model', {})

          if (notification.progress === 1 && model.id) {
            this.setPublishing(model.id, false)
            this.publishingStatus[model.id] = false
            
            this.publication = _.merge(publication, model);
            try {
             const response = await api.getPublicationData({ project: this.project, publication: model })
             if (this.publication && this.publication.id === model.id) {
               this.publication = _.merge({}, this.publication, response.publication)
               console.log('REFRESHED PUBLICATION DATA', this.publication, response)
             }
            } catch (e) {
              console.error('Failed to refresh publication data', e)
            }
          }
        }
      )
    },
    async updateProjectConfiguration(event) {
      let configuration = event.target.value;
      if (_.isString(configuration) && !configuration.length) {
        configuration = null
      } else {
        configuration = JSON.parse(configuration)
      }

      // api call to save project configuration
      const result = await api.updateProjectConfiguration(
        this.project.id,
        configuration
      )

      if (result.errors) {
        // catch errors
        console.error(result.errors)
      } else {
        // update publication
        this.project.configuration = result.configuration
      }
    },
    async updateConfiguration(id, event) {
      let configuration = event.target.value;
      this.setPublicationConfigSaving(this.publication.id, true)

      if (_.isString(configuration) && !configuration.length) {
        configuration = null
      } else {
        configuration = JSON.parse(configuration)
      }

      // api call to save configuration
      const result = await api.saveConfig(this.publication.id, { id, configuration })
      this.publication.content_updated_at = result.content_updated_at

      this.setPublicationConfigSaving(this.publication.id, false)
    },
    async updatePreviewLinkName(id, event) {
      var linkName = event.target.value;
      if (_.isString(linkName) && !linkName.length) {
        linkName = ''
      }

      event.target.value = 'Saving..'

      // api call to save configuration
      await this.publicPreviewNameUpdate({ id, linkName })
    },
    async save(publication) {
      this.setSaving('new-publication')

      // api call to save
      const result = await api.create(this.project.id, publication)

      if (result.errors) {
        // catch errors
        this.setErrors('new-publication', result)
      } else {
        // append new publication to list
        this.publication.push(result)

        this.setupEchoListener(result)

        // clear temp publication
        this.isCreating = false
        this.setEditing(result.id, false)
      }

      this.setSaving('new-publication', false)
    },
    async update(publication) {
      if (!publication.id)
        return

      this.setSaving(publication.id)

      // api call to save
      const result = await api.update(publication.id, publication)

      if (result.errors) {
        // catch errors
        this.setErrors(publication.id, result)
      } else {
        // update publication
        this.publication = result
        this.setupEchoListener(result)
      }

      this.setSaving(publication.id, false)
    },
    async remove(publication) {
      if (!confirm('Are you sure you want to delete this publication?')) {
        return
      }

      // api call to save
      const result = await api.destroy(publication.id)

      if (result.errors) {
        // catch errors
        this.setErrors(publication.id, result);
      } else {
        window.location.href = '/projects/' + this.project.id + '/publications'
      }
    },
    async publish(publication, locale) {
      this.setPublishing(publication.id)

      let data = locale ? ((locale === 'ALL') ? { all: true } : { locale: locale }) : null;

      // api call to process
      const result = await api.publish(publication.id, data)

      if (result.errors) {
        // catch errors
        this.setErrors(publication.id, result)
      } else {

        // process publication
        this.publication = result
        this.setupEchoListener(result)
      }
    },
    async publicPreviewNameUpdate(link) {
      //this.setLinkNameSaving(link.id, true)


      var data = {};
      data['id'] = link.id;
      data['name'] = link.linkName;

      // api call to save
      const result = await api.publicPreviewLinkUpdate(link.id, data)

      if (result.errors) {
        // catch errors
        this.setErrors(link.id, result)
      } else {
        // update publication
        this.publication = result
        //this.setupEchoListener(result)
      }

      this.setLinkNameSaving(link.id, false)
    },

    async bulkExport() {
      this.isExporting = true
      const result = await api.bulkExport(this.project.id)
      this.isExporting = false
    },
    async bulkPackageDownload() {
      this.isPublishAndDownload = true
      const result = await api.bulkPackage(this.project.id)
      this.isPublishAndDownload = false
    },
    async createLink(id, locale = null) {
      this.isLinkCreating = true;
      const result = await api.createPublicLink(id, locale)

      if (result.errors) {
        // catch errors
        console.error(result.errors)
      } else {
        this.updatePublication(id, { links: result })
      }
      this.isLinkCreating = false;
    },
    async resetLink(link) {
      if (!confirm('Are you sure you want to reset this link\'s expiration?')) {
        return
      }

      this.setLinkResetting(link, true);
      const result = await api.resetPublicLink(link);

      if (result.errors) {
        // catch errors
        console.error(result.errors)
      } else {
        this.updatePublication(this.publication.id, { links: result })
      }
      this.setLinkResetting(link, false);
    },
    async removeLink(link) {
      if (!confirm('Are you sure you want to delete this link?')) {
        return
      }

      this.setLinkRemoving(link, true);
      const result = await api.deletePublicLink(link)
      if (result.errors) {
        // catch errors
        console.error(result.errors)
      } else {
        this.updatePublication(this.publication.id, { links: result })
      }
      this.setLinkRemoving(link, false);
    },
    async copyLink(link, link_url) {
      link_url = link_url ?? link.url
      this.setLinkCopying(link.id, true);
      await navigator.clipboard.writeText(link_url);
      this.setLinkCopying(link.id, false);
    },
    async lockLink(link, lockedFlag) {
      if (lockedFlag == 0) {
        if (!confirm('Are you sure you want to lock this link?')) {
          return
        }
      } else if (lockedFlag == 2) {
        if (!confirm('Are you sure you want to unlock this link?')) {
          return
        }
      } else{
        return
      }

      //this.setLinkLocking(link, lockedFlag);
      const result = await api.lockPublicLink(link)
      if (result.errors) {
        // catch errors
        console.error(result.errors)
      } else {
        this.updatePublication(this.publication.id, { links: result })
      }
      //this.setLinkLocking(link.id, link.is_locked);
    },
    onPreviewPressed(locale) {
      if (locale) {
        window.open(this.publication.preview_url + '?locale=' + locale, '_blank')
      } else {
        window.open(this.publication.preview_url, '_blank')
      }
    },
    async deleteLocalizedZip(publicationZipId) {

      // api call to process
      const result = await api.deleteLocalizedZip(publicationZipId)

      if (result.errors) {
        // catch errors
        this.setErrors(this.publication.id, result)
      } else {

        // process publication
        this.publication = result
        this.setupEchoListener(result)
      }
    },
    updatePublication(id, data) {
      const result = Object.assign({}, this.publication, data)
      this.publication = result
    }
  },
  created() {
    const status = {}
    this.setupEchoListener(this.item)
    this.publishingStatus = status

    this.$store.commit('builder/setSourceType', 'project')

    this.$store.commit('builder/setSource', this.project)

    this.$store.commit(
      'builder/setDataObjectSource',
      this.project.data_objects
    )
    this.$store.commit(
      'builder/setDataObjects',
      _.cloneDeep(this.project.data_objects)
    )

    this.$store.commit('builder/setProject', this.project)
    this.$store.commit('builder/setDatasets', this.project.data_objects)
  }
}
</script>
